import { useRef, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Search, Cpu, GraduationCap } from "lucide-react";
import { useTranslation } from "@/lib/i18n";

const icons = [Search, Cpu, GraduationCap];

const ServicesSection = () => {
  const prefersReduced = useReducedMotion();
  const t = useTranslation();
  const s = t.aiTeams.services;
  const isFinePointer = typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches;

  const ctaRef = useRef<HTMLAnchorElement>(null);
  const ctaContainerRef = useRef<HTMLDivElement>(null);

  const anim = {
    initial: prefersReduced ? { opacity: 0 } : { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" as const },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
  };

  const handleCardMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isFinePointer) return;
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--card-mx", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--card-my", `${e.clientY - rect.top}px`);
  }, [isFinePointer]);

  const handleCtaMove = useCallback((e: React.MouseEvent) => {
    if (prefersReduced || !isFinePointer || !ctaRef.current || !ctaContainerRef.current) return;
    const r = ctaContainerRef.current.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = (e.clientX - cx) * 0.08;
    const dy = (e.clientY - cy) * 0.08;
    const max = 8;
    ctaRef.current.style.transform = `translate(${Math.max(-max, Math.min(max, dx))}px, ${Math.max(-max, Math.min(max, dy))}px)`;
  }, [prefersReduced, isFinePointer]);

  const handleCtaLeave = useCallback(() => {
    if (ctaRef.current) ctaRef.current.style.transform = "translate(0,0)";
  }, []);

  return (
    <section id="services" className="py-16 bg-card/40">
      <div className="container mx-auto px-6">
        <motion.div
          initial={anim.initial}
          whileInView={anim.whileInView}
          viewport={anim.viewport}
          transition={anim.transition}
          className="mb-16"
        >
          <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">{s.eyebrow}</p>
          <h2 className="text-4xl sm:text-5xl font-display tracking-tight mb-4">{s.heading}</h2>
          <p className="text-muted-foreground font-body">{s.sub}</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {s.cards.map((service, i) => {
            const Icon = icons[i];
            return (
              <motion.div
                key={service.title}
                initial={anim.initial}
                whileInView={anim.whileInView}
                viewport={anim.viewport}
                transition={{ delay: prefersReduced ? 0 : i * 0.08, ...anim.transition }}
                onMouseMove={handleCardMove}
                className={`relative rounded-2xl border bg-card/60 p-8 overflow-hidden transition-[border-color,transform,box-shadow,background-color] duration-300 hover:border-primary/50 [@media(pointer:fine)]:hover:-translate-y-1 hover:bg-card/80 ${
                  i === 1 ? "border-primary/30" : "border-border"
                }`}
                style={isFinePointer ? {
                  background: "radial-gradient(600px circle at var(--card-mx, 50%) var(--card-my, 50%), hsl(var(--primary) / 0.06), transparent 60%)",
                } : undefined}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary flex-shrink-0">
                    <Icon size={20} />
                  </div>
                  <h3 className="text-xl font-display min-w-0">{service.title}</h3>
                </div>
                <p className="text-muted-foreground font-body text-sm leading-relaxed">{service.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={anim.initial}
          whileInView={anim.whileInView}
          viewport={anim.viewport}
          transition={{ delay: 0.3, ...anim.transition }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-6 border-t border-border"
          ref={ctaContainerRef}
          onMouseMove={handleCtaMove}
          onMouseLeave={handleCtaLeave}
        >
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-display tabular-nums text-gradient-premium">{s.price}</span>
            </div>
          </div>
          <a
            ref={ctaRef}
            href="#book"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-body font-semibold text-sm hover:bg-primary/90 transition-all group/link"
            style={{ willChange: "transform" }}
          >
            {s.cta}
            <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
